import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Eye, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TravelerAROverlay({ attractionId, attractionTitle, onClose }) {
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        const fetchAssets = async () => {
            try {
                const res = await fetch(`${API}/api/traveler/attractions/${attractionId}/ar`, { credentials: 'include' });
                if (res.ok) {
                    const data = await res.json();
                    setAssets(data);
                }
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        if (attractionId) fetchAssets();
    }, [attractionId]);

    if (loading) {
        return (
            <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
                <div className="animate-pulse text-white/40">Loading AR experience...</div>
            </div>
        );
    }

    if (assets.length === 0) {
        return (
            <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" data-testid="ar-overlay-empty">
                <div className="text-center p-8">
                    <Eye className="w-12 h-12 text-white/20 mx-auto mb-3" />
                    <p className="text-white/60">No AR content available for this attraction yet</p>
                    <button onClick={onClose} className="mt-4 px-6 py-2 bg-white/10 rounded-full text-white/70 hover:bg-white/20 text-sm">Close</button>
                </div>
            </div>
        );
    }

    const asset = assets[currentIndex];
    const prev = () => setCurrentIndex(i => (i - 1 + assets.length) % assets.length);
    const next = () => setCurrentIndex(i => (i + 1) % assets.length);

    return (
        <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="ar-overlay">
            {/* Top bar */}
            <div className="flex items-center justify-between px-4 py-3 bg-black/80 backdrop-blur-sm">
                <div className="flex items-center gap-2">
                    <Eye className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-sm font-semibold text-white">{attractionTitle}</span>
                </div>
                <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="ar-close">
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* Main content */}
            <div className="flex-1 relative flex items-center justify-center overflow-hidden">
                {/* Background image */}
                {asset.image_url && (
                    <img
                        src={asset.image_url}
                        alt={asset.title}
                        className="absolute inset-0 w-full h-full object-contain"
                        data-testid="ar-image"
                    />
                )}

                {/* Year badge */}
                {asset.year_label && (
                    <div className="absolute top-4 left-4 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm rounded-full px-3 py-1.5" data-testid="ar-year">
                        <Clock className="w-3.5 h-3.5 text-[#D4AF37]" />
                        <span className="text-sm font-bold text-[#D4AF37]">{asset.year_label}</span>
                    </div>
                )}

                {/* Info overlay card */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/70 to-transparent pt-16 pb-6 px-6" data-testid="ar-info">
                    <div className="max-w-lg mx-auto">
                        {/* Type badge */}
                        <span className="inline-block px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-medium rounded-full mb-2 capitalize">
                            {asset.type?.replace('_', ' ')}
                        </span>
                        <h3 className="text-xl font-bold text-white mb-2">{asset.title}</h3>
                        {asset.description && (
                            <p className="text-sm text-white/70 leading-relaxed mb-3">{asset.description}</p>
                        )}
                        {asset.overlay_text && (
                            <div className="bg-white/5 border-l-2 border-[#D4AF37] pl-3 py-2 rounded-r-lg">
                                <p className="text-sm text-white/80 italic">{asset.overlay_text}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Navigation arrows */}
                {assets.length > 1 && (
                    <>
                        <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm" data-testid="ar-prev">
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm" data-testid="ar-next">
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </>
                )}
            </div>

            {/* Bottom dots */}
            {assets.length > 1 && (
                <div className="flex items-center justify-center gap-2 py-3 bg-black/80">
                    {assets.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setCurrentIndex(i)}
                            className={`w-2 h-2 rounded-full transition-all ${i === currentIndex ? 'bg-[#D4AF37] w-6' : 'bg-white/20'}`}
                            data-testid={`ar-dot-${i}`}
                        />
                    ))}
                    <span className="ml-3 text-xs text-white/40">{currentIndex + 1} / {assets.length}</span>
                </div>
            )}
        </div>
    );
}
